import { useMutation } from "@apollo/client"
import { Add, AddBox, Delete, Email, Person, Phone } from "@mui/icons-material"
import ImageIcon from "@mui/icons-material/Image"
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  TextField,
  Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { StyledButton } from "components/Button/StyledButton"
import { PDFDropzone } from "components/Dropzone/PDFDropzone"
import { HaufeIcon } from "components/HaufeIcon/HaufeIcon"
import { HaufeOverlay } from "components/HaufeLoader/HaufeLoader"
import { ImageDropzone } from "components/ImageDropZone/ImageDropzone"
import { ImageThumbnail } from "components/ImageThumbnail/ImageThumbnail"
import { HAUFE_COMPANY_ID } from "config"
import { useHaufeLoading } from "contexts/HaufeLoadingContext"
import dayjs from "dayjs"
import { DELETE_FILE, UPLOAD_FILE } from "graphql/queries"
import {
  Contact,
  CreateSalesQuoteFromOpportunityInput,
  Device,
  FileType,
  FileUploadInput,
  Lead,
  Project,
  ProjectContact,
  File as ProjectFile,
  SalesQuote,
  SalesQuoteWithDetails,
  User,
} from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { useEffect, useRef, useState } from "react"
import { FileRejection } from "react-dropzone"
import { useNavigate } from "react-router"
import { emptyPerson } from "store/store"
import { isEutGroup } from "store/typeGuards"
import { generateProjectCalculation } from "utils/generateProjectCalculation"
import { getImageURL } from "utils/getFileURL"
import { useLocalStorage } from "utils/useLocalStorage"
import { v4 } from "uuid"
import { getPDFUrl } from "../../../store/pdfService"
import { AddBusinessContactDialog } from "./AddBusinessContactDialog"
import { AddProjectManagerDialog } from "./AddProjectManagerDialog"

type ProjectDetailGeneralDataProps = {
  project: Project
  lead: Lead
  onSave: (project: Project) => void
  onSaveDevices: (devices: Device, isOffer: boolean) => void
  businessAccount: string
  onTestSetupSave?: (updatedProject: Project) => Promise<void>
  onTestSetupCancel?: () => void
  hasTestSetupChanges?: () => boolean
}

export const ProjectDetailGeneralData = ({
  project,
  lead,
  onSave,
  onSaveDevices,
  businessAccount,
  onTestSetupSave,
  onTestSetupCancel,
  hasTestSetupChanges,
}: ProjectDetailGeneralDataProps) => {
  const { contacts } = useStoreState((state) => state)
  const {
    updateSalesQuote,
    getSalesQuotesByOpportunityIDs,
    fetchSalesQuotes,
    createSalesQuote,
    getSalesQuoteByQuoteNbrWithDetails,
  } = useStoreActions((action) => action)

  const { isHaufeLoading, haufeOperationType } = useHaufeLoading()

  const [updatedProject, setUpdatedProject] = useState<Project>(project)
  const [hasUserMadeChanges, setHasUserMadeChanges] = useState<boolean>(false)
  const [pdfFiles, setPdfFiles] = useState<File[]>([])
  const [pdfBase64Strings, setPdfBase64Strings] = useState<string[]>([])
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [imageBase64Strings, setImageBase64Strings] = useState<string[]>([])
  // TODO: Figure out PDF dialog implementation later
  const [filesToDelete, setFilesToDelete] = useState<string[]>([])
  const [uploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false)
  const uploadDialogStateRef = useRef({
    pdfFiles: [] as File[],
    pdfBase64: [] as string[],
    imageFiles: [] as File[],
    imageBase64: [] as string[],
    hasChanges: false,
  })

  const [uploadFile] = useMutation(UPLOAD_FILE)
  const [deleteFile] = useMutation(DELETE_FILE)

  const [creatingOffer, setCreatingOffer] = useState<boolean>(false)
  const [user] = useLocalStorage<User>("user", emptyPerson)

  const [openAddContactDialog, setOpenAddContactDialog] = useState<boolean>(false)
  const [openAddProjectManagerDialog, setOpenAddProjectManagerDialog] = useState<boolean>(false)

  const [openUnsavedChangesDialog, setOpenUnsavedChangesDialog] = useState<boolean>(false)

  const navigate = useNavigate()

  useEffect(() => {
    // Only update if the user hasn't made changes to preserve their input
    if (!hasUserMadeChanges) {
      setUpdatedProject(project)
    }
  }, [project, hasUserMadeChanges])

  const handleDeleteFiles = async () => {
    if (filesToDelete.length > 0) {
      try {
        await deleteFile({ variables: { ids: filesToDelete } })
      } catch (error) {
        console.error("Fehler beim Löschen der Datei:", error)
      }
      setFilesToDelete([])
    }
  }

  const handleOpenUploadDialog = () => {
    uploadDialogStateRef.current = {
      pdfFiles: [...pdfFiles],
      pdfBase64: [...pdfBase64Strings],
      imageFiles: [...imageFiles],
      imageBase64: [...imageBase64Strings],
      hasChanges: hasUserMadeChanges,
    }
    setUploadDialogOpen(true)
  }

  const handleCancelUploadDialog = () => {
    const previousState = uploadDialogStateRef.current
    setPdfFiles(previousState.pdfFiles)
    setPdfBase64Strings(previousState.pdfBase64)
    setImageFiles(previousState.imageFiles)
    setImageBase64Strings(previousState.imageBase64)
    setHasUserMadeChanges(previousState.hasChanges)
    setUploadDialogOpen(false)
  }

  const handleConfirmUploadDialog = () => {
    setUploadDialogOpen(false)
  }

  const handleUploadFiles = async () => {
    const newDocuments: { id: string; name: string; type: FileType }[] = []

    const uploads = [
      ...pdfFiles.map((file, index) => ({
        file,
        base64: pdfBase64Strings[index],
        type: FileType.PDF,
      })),
      ...imageFiles.map((file, index) => ({
        file,
        base64: imageBase64Strings[index],
        type: FileType.IMAGE,
      })),
    ]

    for (const upload of uploads) {
      const { file, base64, type } = upload
      if (file && base64) {
        const fileInput: FileUploadInput = {
          base64,
          createdAt: new Date().toISOString(),
          createdBy: user.id,
          filename: file.name,
          id: "NEW",
          name: file.name.split(".")[0],
          type,
        }

        try {
          const result = await uploadFile({ variables: { input: fileInput } })
          if (result.data) {
            newDocuments.push({ id: result.data.uploadFile, name: file.name, type })
          }
        } catch (error) {
          console.error("Fehler beim Hochladen der Datei:", error)
        }
      }
    }

    return newDocuments
  }

  const handleSave = async () => {
    handleDeleteFiles()
    const newDocuments = await handleUploadFiles()

    const allDocuments = [
      ...updatedProject.documents.map((doc) => ({
        id: doc.id,
        name: doc.name,
        type: doc.type,
      })),
      ...newDocuments,
    ]

    const newProject = {
      ...updatedProject,
      documents: allDocuments.map((item) => ({
        id: item.id,
        name: item.name,
        filename: "",
        fileURL: "",
        type: item.type || FileType.PDF,
        createdAt: new Date().toISOString(),
        createdBy: "",
        description: "",
      })),
    }

    // Clear the uploaded files after successful save
    setPdfFiles([])
    setPdfBase64Strings([])
    setImageFiles([])
    setImageBase64Strings([])

    setUpdatedProject(newProject)
    setHasUserMadeChanges(false)

    // Save TestSetup changes if there are any, passing the updated project
    if (hasTestSetupChanges && hasTestSetupChanges() && onTestSetupSave) {
      await onTestSetupSave(newProject)
    } else {
      // Only save project if no TestSetup changes
      onSave(newProject)
    }
  }

  const handleCancel = () => {
    // First cancel TestSetup changes if there are any
    if (hasTestSetupChanges && hasTestSetupChanges() && onTestSetupCancel) {
      onTestSetupCancel()
    }

    setUpdatedProject(project)
    setPdfFiles([])
    setPdfBase64Strings([])
    setImageFiles([])
    setImageBase64Strings([])
    setFilesToDelete([])
    setHasUserMadeChanges(false)
  }

  const handleImageFileSelect = async (files: File[]) => {
    if (files.length === 0) {
      setImageFiles([])
      setImageBase64Strings([])
      return
    }

    try {
      const base64Strings = await Promise.all(
        files.map(
          (file) =>
            new Promise<string>((resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result as string)
              reader.onerror = (event) => reject(event)
              reader.readAsDataURL(file)
            })
        )
      )

      setImageFiles(files)
      setImageBase64Strings(base64Strings)
      setHasUserMadeChanges(true)
    } catch (error) {
      console.error("Fehler beim Verarbeiten der Bilddatei:", error)
      enqueueSnackbar("Bild konnte nicht verarbeitet werden.", { variant: "error" })
    }
  }

  const handleImageFileReject = (rejections: FileRejection[]) => {
    if (rejections.length === 0) {
      return
    }

    const messages = rejections
      .map(
        (rejection) =>
          `${rejection.file.name}: ${rejection.errors.map((err) => err.message).join(", ")}`
      )
      .join("; ")

    enqueueSnackbar(`Bild konnte nicht hochgeladen werden: ${messages}`, { variant: "error" })
  }

  const handleRemovePendingImage = (index: number) => {
    const updatedFiles = [...imageFiles]
    const updatedBase64 = [...imageBase64Strings]

    updatedFiles.splice(index, 1)
    updatedBase64.splice(index, 1)

    setImageFiles(updatedFiles)
    setImageBase64Strings(updatedBase64)

    setHasUserMadeChanges(true)
  }

  const handleRemovePendingPdf = (index: number) => {
    const updatedFiles = [...pdfFiles]
    const updatedBase64 = [...pdfBase64Strings]

    updatedFiles.splice(index, 1)
    updatedBase64.splice(index, 1)

    setPdfFiles(updatedFiles)
    setPdfBase64Strings(updatedBase64)

    setHasUserMadeChanges(true)
  }

  const handleFileSelect = (files: File[], base64Strings: string[]) => {
    setPdfFiles(files)
    setPdfBase64Strings(base64Strings)
    setHasUserMadeChanges(true)
  }

  const handleCreateSalesQuote = async (opportunityID: string) => {
    if (!opportunityID) {
      enqueueSnackbar("Angebot kann nicht erstellt werden: Opportunity-ID fehlt.", {
        variant: "error",
      })
      return
    }
    try {
      const payload: CreateSalesQuoteFromOpportunityInput = {
        opportunityID: opportunityID,
        description: `Angebot für ${opportunityID}`,
      }

      console.log("Creating sales quote from project...", payload)
      const result = await createSalesQuote(payload)

      if (result && result.quoteNbr) {
        enqueueSnackbar("Angebot erfolgreich erstellt!", {
          variant: "success",
        })
        // Refresh quotes to ensure we have the latest data
        await fetchSalesQuotes({ fresh: true })
      } else {
        // Handle case where quoteNbr is not immediately available (e.g., 202 response)
        enqueueSnackbar("Angebotserstellung eingeleitet. Bitte prüfen Sie die Angebotsliste.", {
          variant: "success",
        })
        console.log("Sales Quote created but quoteNbr not immediately available:", result)
        // Refresh quotes and redirect to sales quotes list
        await fetchSalesQuotes({ fresh: true })
      }
    } catch (err: any) {
      console.error("Failed to create sales quote:", err)
      enqueueSnackbar(
        `Fehler beim Erstellen des Angebots: ${err.message || "Unbekannter Fehler"}`,
        {
          variant: "error",
        }
      )
    }
  }

  const handleCreateOffer = async () => {
    if (!updatedProject.devices || !updatedProject.lead.opportunityID) {
      return
    }

    // Check for unsaved changes
    if (hasUnsavedChanges()) {
      setOpenUnsavedChangesDialog(true)
      return
    }

    await executeCreateOffer()
  }

  const executeCreateOffer = async () => {
    if (!updatedProject.devices || !updatedProject.lead.opportunityID) {
      return
    }

    setCreatingOffer(true)
    try {
      let salesQuoteNew: SalesQuote | undefined = undefined

      await handleCreateSalesQuote(updatedProject.lead.opportunityID)

      // Try to find the sales quote with a retry mechanism
      let retryCount = 0
      const maxRetries = 3
      const retryDelay = 2000 // 2 seconds

      while (retryCount < maxRetries) {
        const grouped = await getSalesQuotesByOpportunityIDs({
          opportunityIDs: [updatedProject.lead.opportunityID],
          fresh: true,
        })
        const salesQuotesNew: SalesQuote[] = grouped && grouped[0] ? grouped[0] : []
        salesQuoteNew = salesQuotesNew.find((s) => s.primary)

        if (salesQuoteNew && salesQuoteNew.quoteNbr) {
          break // Found quote with quote number, exit retry loop
        }

        retryCount++
        if (retryCount < maxRetries) {
          console.log(
            `Sales quote not ready yet, retrying in ${retryDelay / 1000} seconds... (${retryCount}/${maxRetries})`
          )
          await new Promise((resolve) => setTimeout(resolve, retryDelay))
        }
      }

      const currentSalesQuote = salesQuoteNew
      if (!currentSalesQuote) {
        throw new Error("Could not find or create sales quote")
      }

      // Check if quoteNbr is available before proceeding with update
      if (!currentSalesQuote.quoteNbr) {
        enqueueSnackbar(
          "Angebot erstellt, aber Angebotsnummer noch nicht verfügbar. Das Angebot wird möglicherweise noch verarbeitet. Bitte prüfen Sie die Angebotsseite.",
          {
            variant: "warning",
          }
        )
        // Redirect to projects page without updating
        navigate("/projectportal/projects")
        return
      }

      const details: SalesQuoteWithDetails = await getSalesQuoteByQuoteNbrWithDetails({
        quoteNbr: currentSalesQuote.quoteNbr,
      })

      const salesQuoteInput = generateProjectCalculation(
        updatedProject,
        currentSalesQuote,
        details.details
      )

      // Additional validation before update
      if (!salesQuoteInput.quoteNbr) {
        throw new Error("Quote Number is required for updating sales quotes")
      }

      await updateSalesQuote(salesQuoteInput)

      // Create a map to track examination IDs to ensure identical examinations get the same ID
      const examinationIdMap = new Map<string, string>()

      const newDevices = updatedProject.devices.map((device) => {
        const newDevice = { ...device }
        if (isEutGroup(newDevice)) {
          newDevice.examinations = newDevice.examinations.map((exam) => {
            // Create a unique key for this examination based on its properties AND original ID
            // This ensures identical examinations that had the same original ID get the same new ID
            const examKey = `${exam.name}_${exam.examinationConfig?.id}_${exam.norm?.id || "no-norm"}_${exam.id}`

            // Check if we already have an ID for this examination
            let newExamId = examinationIdMap.get(examKey)
            if (!newExamId) {
              newExamId = v4()
              examinationIdMap.set(examKey, newExamId)
            }

            return {
              ...exam,
              id: newExamId,
              salesQuote: currentSalesQuote.quoteNbr,
            }
          })
        }
        return newDevice
      })

      newDevices.forEach((device) => {
        onSaveDevices(device, true)
      })

      console.log("Current Sales Quote", currentSalesQuote)

      // Open Haufe page in new window
      const haufeUrl = `https://treo.haufe-x360.app/(W(7))/Main?CompanyID=${HAUFE_COMPANY_ID}&ScreenId=CR304500&OpportunityID=${updatedProject.lead.opportunityID}&QuoteNbr=${currentSalesQuote.quoteNbr}`
      window.open(haufeUrl, "_blank")

      // Redirect current window to projects page
      navigate("/projectportal/projects")
    } catch (err: any) {
      console.error("Failed to process sales quote:", err)
      enqueueSnackbar(
        `Fehler beim Verarbeiten des Angebots: ${err.message || "Unbekannter Fehler"}`,
        {
          variant: "error",
        }
      )
    } finally {
      setCreatingOffer(false)
    }
  }

  const handleRemoveDocument = (docId: string) => {
    setUpdatedProject((prevState) => {
      const updatedDocuments = prevState.documents.filter((doc) => doc.id !== docId)
      return { ...prevState, documents: updatedDocuments }
    })
    setFilesToDelete([...filesToDelete, docId])
    setHasUserMadeChanges(true)
  }

  const openDocument = async (document: ProjectFile) => {
    try {
      if (document.type === FileType.IMAGE) {
        const imageUrl = getImageURL(document.id)
        if (!imageUrl) {
          throw new Error("Kein Bildlink verfügbar")
        }
        window.open(imageUrl, "_blank")
        return
      }

      const pdfUrl = await getPDFUrl(document.id)
      window.open(pdfUrl, "_blank")
    } catch (error) {
      console.error("Error opening document:", error)
      enqueueSnackbar(`Fehler beim Öffnen des Dokuments: ${document.name}`, {
        variant: "error",
      })
    }
  }

  const handleAddContact = (contact: Contact, type: string) => {
    if (contact.contactID) {
      let existingContact = updatedProject.contacts.find(
        (item) => item.contactID === contact.contactID && item.type === type
      )
      if (existingContact) {
        return
      }

      existingContact = updatedProject.contacts.find((item) => item.contactID === contact.contactID)

      let newContact: ProjectContact[] = []
      if (existingContact) {
        newContact = updatedProject.contacts.map((item) => {
          if (item.contactID === contact.contactID) {
            return { contactID: contact.contactID, type: type }
          }
          return item
        })
      } else {
        newContact = [...updatedProject.contacts, { contactID: contact.contactID, type: type }]
      }

      setUpdatedProject({
        ...updatedProject,
        contacts: newContact,
      })
      setHasUserMadeChanges(true)
    }
  }

  const handleSaveAndCreateOffer = async () => {
    setOpenUnsavedChangesDialog(false)
    await handleSave()
    await executeCreateOffer()
  }

  const handleCancelCreateOffer = () => {
    setOpenUnsavedChangesDialog(false)
  }

  const handleRemoveContact = (contactID: string) => {
    setUpdatedProject((prevState) => {
      const updatedContacts = prevState.contacts.filter((item) => item.contactID !== contactID)
      return { ...prevState, contacts: updatedContacts }
    })
    setHasUserMadeChanges(true)
  }

  const handleProjectManagerSave = (manager: User) => {
    setUpdatedProject({
      ...updatedProject,
      management: manager,
    })
    setHasUserMadeChanges(true)
    setOpenAddProjectManagerDialog(false)
  }

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    try {
      return (
        hasUserMadeChanges ||
        pdfFiles.length > 0 ||
        filesToDelete.length > 0 ||
        updatedProject.id === "NEW" ||
        (hasTestSetupChanges && hasTestSetupChanges())
      )
    } catch (error) {
      // If TestSetup changes check fails, just return the other conditions
      return (
        hasUserMadeChanges ||
        pdfFiles.length > 0 ||
        filesToDelete.length > 0 ||
        updatedProject.id === "NEW"
      )
    }
  }

  return (
    <>
      {/* Haufe Overlay for Sales Quote Creation */}
      <HaufeOverlay
        open={isHaufeLoading && (haufeOperationType?.includes("Angebot") || creatingOffer)}
        message="Angebot wird in Haufe erstellt..."
        compact={true}
      />

      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          height: "calc(100vh - 277px)",
          overflow: "auto",
        }}
      >
        <TextField
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Beschreibung & Zeitplan <HaufeIcon />
            </Box>
          }
          name="description"
          value={updatedProject.description}
          onChange={(e) => {
            setUpdatedProject({
              ...updatedProject,
              description: e.target.value,
            })
            setHasUserMadeChanges(true)
          }}
          multiline
          minRows={4}
        />

        <TextField
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Sonderabsprachen <HaufeIcon />
            </Box>
          }
          name="specialArrangements"
          value={updatedProject.specialArrangements}
          onChange={(e) => {
            setUpdatedProject({
              ...updatedProject,
              specialArrangements: e.target.value,
            })
            setHasUserMadeChanges(true)
          }}
          multiline
          minRows={4}
        />
        <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
          <DatePicker
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                Plantermin (Start) <HaufeIcon />
              </Box>
            }
            value={updatedProject.plannedDate ? dayjs(updatedProject.plannedDate) : null}
            onChange={(value) => {
              setUpdatedProject({
                ...updatedProject,
                plannedDate: value?.format("YYYY-MM-DD") || "",
              })
              setHasUserMadeChanges(true)
            }}
            slotProps={{
              textField: {
                fullWidth: true,
              },
            }}
          />

          <DatePicker
            disabled
            label="Wunschtermin"
            value={updatedProject.desiredDate ? dayjs(updatedProject.desiredDate) : null}
            onChange={(value) => {
              setUpdatedProject({
                ...updatedProject,
                desiredDate: value?.format("YYYY-MM-DD") || "",
              })
              setHasUserMadeChanges(true)
            }}
            slotProps={{
              textField: {
                fullWidth: true,
              },
            }}
          />
        </Box>

        <Box>
          <Typography variant="subtitle1">Verkäufer</Typography>
          {updatedProject.sales.displayName && (
            <Card sx={{ mb: 1, backgroundColor: "#353744", color: "white" }}>
              <CardHeader
                avatar={
                  <Avatar alt={updatedProject.sales.displayName || ""}>
                    {updatedProject.sales.displayName?.charAt(0)}
                  </Avatar>
                }
                title={updatedProject.sales.displayName}
                subheader={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {updatedProject.sales.email && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Email fontSize="small" />
                        <Typography variant="body2">
                          <Link
                            href={`mailto:${updatedProject.sales.email}`}
                            sx={{ color: "white" }}
                          >
                            {updatedProject.sales.email}
                          </Link>
                        </Typography>
                      </Box>
                    )}
                    {!updatedProject.sales.email && (
                      <Typography variant="body2">Keine E-Mail verfügbar</Typography>
                    )}
                  </Box>
                }
              />
            </Card>
          )}
          {!updatedProject.sales.displayName && (
            <Box>
              <Typography variant="body2">Kein Verkäufer zugewiesen</Typography>
            </Box>
          )}
        </Box>

        <Box>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}
          >
            <Typography variant="subtitle1">Projektleitung</Typography>
          </Box>
          {updatedProject.management?.displayName ? (
            <Card sx={{ mb: 2, backgroundColor: "#353744", color: "white" }}>
              <CardHeader
                avatar={
                  <Avatar alt={updatedProject.management.displayName}>
                    {updatedProject.management.displayName.charAt(0)}
                  </Avatar>
                }
                title={updatedProject.management.displayName}
                subheader={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {updatedProject.management.email && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Email fontSize="small" />
                        <Typography variant="body2">
                          <Link
                            href={`mailto:${updatedProject.management.email}`}
                            sx={{
                              color: "white",
                              textDecoration: "underline",
                            }}
                          >
                            {updatedProject.management.email}
                          </Link>
                        </Typography>
                      </Box>
                    )}
                    {!updatedProject.management.email && (
                      <Typography variant="body2">Keine E-Mail verfügbar</Typography>
                    )}
                  </Box>
                }
                action={
                  <IconButton
                    onClick={() => setOpenAddProjectManagerDialog(true)}
                    sx={{ color: "white" }}
                  >
                    <Person />
                  </IconButton>
                }
              />
            </Card>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Kein Projektleiter zugewiesen
              </Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-around" }}>
            <IconButton
              onClick={() => setOpenAddProjectManagerDialog(true)}
              size="small"
              sx={{
                backgroundColor: "#2c2d33",
                color: "white",
                "&:hover": {
                  backgroundColor: "#353744",
                },
              }}
            >
              {updatedProject.management?.id ? (
                <Person fontSize="small" />
              ) : (
                <AddBox fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle1">Kundenkontakte</Typography>
          {updatedProject.contacts
            .map((contact) => {
              const contactDatas = contacts.find((item) => item.contactID === contact.contactID)
              return { contact, contactDatas }
            })
            .filter((item) => item.contactDatas) // Filter out contacts that haven't loaded yet
            .sort((a, b) => {
              const lastNameA = a.contactDatas?.lastName || ""
              const lastNameB = b.contactDatas?.lastName || ""
              return lastNameA.localeCompare(lastNameB)
            })
            .map(({ contact, contactDatas }) => (
              <Card
                sx={{ mb: 1, backgroundColor: "#353744", color: "white" }}
                key={contact.contactID}
              >
                <CardHeader
                  avatar={
                    <Avatar alt={contactDatas?.lastName || ""}>
                      {contactDatas?.firstName?.charAt(0)}
                      {contactDatas?.lastName?.charAt(0)}
                    </Avatar>
                  }
                  title={`${contactDatas?.firstName} ${contactDatas?.lastName} (${contact.type})`}
                  subheader={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      {contactDatas?.phone1 && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Phone fontSize="small" />
                          <Typography variant="body2">
                            <Link href={`tel:${contactDatas.phone1}`} sx={{ color: "white" }}>
                              {contactDatas.phone1}
                            </Link>
                          </Typography>
                        </Box>
                      )}
                      {contactDatas?.email && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Email fontSize="small" />
                          <Typography variant="body2">
                            <Link href={`mailto:${contactDatas.email}`} sx={{ color: "white" }}>
                              {contactDatas.email}
                            </Link>
                          </Typography>
                        </Box>
                      )}
                      {!contactDatas?.phone1 && !contactDatas?.email && (
                        <Typography variant="body2">Keine Kontaktdaten verfügbar</Typography>
                      )}
                    </Box>
                  }
                  action={
                    <IconButton size="small" onClick={() => handleRemoveContact(contact.contactID)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  }
                />
              </Card>
            ))}
          {updatedProject.contacts.length === 0 && (
            <Box>
              <Typography variant="body2">Keine Kontakte vorhanden</Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-around" }}>
            <IconButton
              onClick={() => setOpenAddContactDialog(true)}
              sx={{
                backgroundColor: "#2c2d33",
                color: "white",
                "&:hover": {
                  backgroundColor: "#353744",
                },
              }}
            >
              <AddBox fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box>
          {updatedProject.documents && updatedProject.documents.length > 0 && (
            <>
              <Typography variant="subtitle1">Dokumente</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                {updatedProject.documents.map((doc) => {
                  const isPdf = doc.type === FileType.PDF
                  const isImage = doc.type === FileType.IMAGE
                  const icon = isImage ? (
                    <ImageIcon />
                  ) : isPdf ? (
                    <PictureAsPdfIcon />
                  ) : (
                    <InsertDriveFileIcon />
                  )

                  return (
                    <Chip
                      key={`existing-doc-${doc.id}`}
                      icon={icon}
                      label={doc.name}
                      onClick={() => openDocument(doc)}
                      onDelete={() => handleRemoveDocument(doc.id)}
                    />
                  )
                })}
              </Box>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          <Box sx={{ mt: 2 }}>
            <StyledButton
              buttonType="custom"
              customIcon={<Add />}
              customText="Dateien hinzufügen"
              variant="outlined"
              onClick={handleOpenUploadDialog}
            />
            {(pdfFiles.length > 0 || imageFiles.length > 0) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Ausgewählte Dateien (noch nicht gespeichert)
                </Typography>
                {pdfFiles.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      mb: imageFiles.length > 0 ? 1 : 0,
                    }}
                  >
                    {pdfFiles.map((file, index) => (
                      <Chip
                        key={`pending-pdf-${file.name}-${index}`}
                        icon={<PictureAsPdfIcon />}
                        label={file.name}
                        onDelete={() => handleRemovePendingPdf(index)}
                      />
                    ))}
                  </Box>
                )}
                {imageFiles.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {imageFiles.map((file, index) => (
                      <Chip
                        key={`pending-image-${file.name}-${index}`}
                        icon={<ImageIcon />}
                        label={file.name}
                        onDelete={() => handleRemovePendingImage(index)}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
          <TextField
            label="Notizen"
            name="notes"
            value={updatedProject.notes}
            onChange={(e) => {
              setUpdatedProject({
                ...updatedProject,
                notes: e.target.value,
              })
              setHasUserMadeChanges(true)
            }}
            multiline
            minRows={4}
            fullWidth
            sx={{ mt: 2, mb: 2 }}
          />
        </Box>
      </CardContent>

      <Dialog open={uploadDialogOpen} maxWidth="md" fullWidth>
        <DialogTitle>Dateien hinzufügen</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                PDFs hochladen
              </Typography>
              <PDFDropzone
                onFileSelect={handleFileSelect}
                selectedFiles={pdfFiles}
                onRemoveFile={handleRemovePendingPdf}
                filesOnTop={false}
              />
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Bilder hochladen
              </Typography>
              <ImageDropzone
                onImageFileChosen={handleImageFileSelect}
                onFileReject={handleImageFileReject}
                accept={{ "image/*": [] }}
                image={null}
                setImage={() => {}}
                fullWidth
                height={220}
              />
              {imageFiles.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {imageFiles.map((file, index) => (
                      <ImageThumbnail
                        key={`dialog-image-${file.name}-${index}`}
                        image={
                          {
                            id: "temp",
                            name: file.name,
                            type: "IMAGE",
                            fileURL: "",
                            filename: file.name,
                            createdAt: new Date().toISOString(),
                            createdBy: "",
                            description: "",
                          } as any
                        }
                        onDelete={() => handleRemovePendingImage(index)}
                        onOpen={undefined}
                        size={120}
                        srcOverride={imageBase64Strings[index]}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <StyledButton buttonType="cancel" onClick={handleCancelUploadDialog} />
          <StyledButton
            buttonType="custom"
            customText="Fertig"
            onClick={handleConfirmUploadDialog}
            variant="contained"
          />
        </DialogActions>
      </Dialog>

      <AddBusinessContactDialog
        open={openAddContactDialog}
        onClose={() => setOpenAddContactDialog(false)}
        businessAccount={businessAccount}
        onSave={handleAddContact}
      />

      <CardActions>
        <StyledButton
          buttonType="save"
          onClick={handleSave}
          variant="contained"
          disabled={!hasUnsavedChanges()}
        />
        <StyledButton buttonType="reset" onClick={handleCancel} disabled={!hasUnsavedChanges()} />
        <StyledButton
          buttonType="custom"
          customIcon={creatingOffer ? <CircularProgress size={18} /> : <Add fontSize="small" />}
          customText={creatingOffer ? "Wird erstellt..." : "Angebot erstellen"}
          onClick={handleCreateOffer}
          disabled={
            creatingOffer ||
            !updatedProject.devices ||
            updatedProject.devices.length === 0 ||
            lead.status === "Won" ||
            lead.status === "Lost"
          }
          variant="contained"
          color="primary"
        />
      </CardActions>

      <Dialog open={openUnsavedChangesDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Ungespeicherte Änderungen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sie haben ungespeicherte Änderungen am Projekt. Möchten Sie diese vor der
            Angebotserstellung speichern?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <StyledButton buttonType="cancel" onClick={handleCancelCreateOffer} />

          <StyledButton
            buttonType="custom"
            customText="Speichern & Erstellen"
            onClick={handleSaveAndCreateOffer}
            variant="contained"
            color="primary"
          />
        </DialogActions>
      </Dialog>

      <AddProjectManagerDialog
        open={openAddProjectManagerDialog}
        onClose={() => setOpenAddProjectManagerDialog(false)}
        currentManager={updatedProject.management}
        onSave={handleProjectManagerSave}
      />
    </>
  )
}
